package fr.frux.core.network.api

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.network.ApiPaths
import fr.frux.shared.dto.AddTreeTraitRequest
import fr.frux.shared.dto.CreateTreeReportRequest
import fr.frux.shared.dto.CreateTreeRequest
import fr.frux.shared.dto.HarvestPartDto
import fr.frux.shared.dto.ObserveTreeStatusRequest
import fr.frux.shared.dto.ResolveTreeReportsRequest
import fr.frux.shared.dto.SharePreviewDto
import fr.frux.shared.dto.SpeciesFormDto
import fr.frux.shared.dto.TreeDto
import fr.frux.shared.dto.TreeListResponse
import fr.frux.shared.dto.TreePhotoDto
import fr.frux.shared.dto.TreeReportResolveActionDto
import fr.frux.shared.dto.TreeStatusDto
import fr.frux.shared.dto.TreeStatusObservationDto
import fr.frux.shared.dto.TreeTraitKindDto
import fr.frux.shared.dto.UpdateTreeRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.onUpload
import io.ktor.client.request.delete
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import kotlin.uuid.Uuid

/**
 * API interface for tree-related operations.
 */
interface TreeApi {
    /**
     * Get nearby trees within radius.
     * @param lat Latitude
     * @param lon Longitude
     * @param radius Radius in kilometers (fractionnaire : la carte l'accorde au zoom)
     * @param page Page number (1-indexed, backend requirement)
     * @param limit Items per page
     * @param form Plant form evaluated by the species reference on the server
     * @param part Harvested part evaluated on the server
     * @param inSeason Keep only species harvestable during the server's current month
     * @param neverSeen Ne rend que les arbres sans passage humain daté (#396) : ni observation
     * de terrain, ni photo visible. Défaut `false`, jamais envoyé dans ce cas (#420).
     *
     * Rend la reponse complete (pas seulement `trees`) : `total` porte le compte serveur, dont
     * la troncature du lot se deduit cote appelant (#155).
     */
    suspend fun getNearbyTrees(
        lat: Double,
        lon: Double,
        radius: Double = 5.0,
        page: Int = 1,
        limit: Int = 20,
        form: SpeciesFormDto? = null,
        part: HarvestPartDto? = null,
        inSeason: Boolean = false,
        rollupId: Long? = null,
        neverSeen: Boolean = false,
        /**
         * Rectangle visible `west,south,east,north` en degres (#621), combine au rayon cote
         * serveur. `null` garde le disque seul.
         */
        bbox: String? = null,
    ): TreeListResponse

    /**
     * Get tree by ID.
     */
    suspend fun getTreeById(id: Long): TreeDto

    /**
     * Aperçu social d'un arbre (#392). Sans consommateur dans l'app à ce jour : les Workers
     * Cloudflare appellent l'endpoint en direct. La méthode existe pour que le contrat soit
     * éprouvé par la suite de contrats, comme toute lecture publique.
     */
    suspend fun sharePreview(id: Long): SharePreviewDto

    /**
     * Create a new tree.
     *
     * [idempotencyKey] rejoue en `Idempotency-Key` (#280) : un défaut aléatoire par appel évite
     * la double création sur un rejeu réseau, un défaut explicite permet à l'appelant de
     * dédupliquer volontairement une nouvelle tentative après timeout.
     */
    suspend fun createTree(
        request: CreateTreeRequest,
        idempotencyKey: String = Uuid.random().toString(),
    ): TreeDto

    /**
     * Adaptateur legacy des builds publiés, ouvert à tout compte connecté (modèle wiki).
     */
    suspend fun updateTree(
        id: Long,
        request: UpdateTreeRequest,
    ): TreeDto

    /**
     * Delete a tree (owner only).
     */
    suspend fun deleteTree(id: Long)

    /**
     * Observe et enregistre le statut courant de l'arbre. Rend 201 [TreeStatusObservationDto] ;
     * le détail expose ensuite `latestObservation` (dernière observation, tous auteurs confondus)
     * et `myObservationStatus` (dernière observation de l'appelant).
     */
    suspend fun observeTreeStatus(
        id: Long,
        status: TreeStatusDto,
    ): TreeStatusObservationDto

    /**
     * Ajoute un trait à l'arbre. Idempotent côté serveur : 201 la première fois, 200 ensuite.
     */
    suspend fun addTreeTrait(
        id: Long,
        kind: TreeTraitKindDto,
    )

    /** Retire le trait [kind] posé par l'appelant. */
    suspend fun removeTreeTrait(
        id: Long,
        kind: TreeTraitKindDto,
    )

    /**
     * Marque l'arbre en favori. Idempotent côté serveur : 201 la première fois, 200 ensuite —
     * les deux sont des succès, aucune exception n'est levée sur un rejeu.
     */
    suspend fun favoriteTree(id: Long)

    /** Retire le favori. Idempotent : 204 même si l'arbre n'était pas favori. */
    suspend fun unfavoriteTree(id: Long)

    /** Favoris de l'utilisateur authentifié, du plus récent au plus ancien. */
    suspend fun getFavoriteTrees(
        page: Int = 1,
        limit: Int = 50,
    ): List<TreeDto>

    /** Arbres créés par l'utilisateur authentifié, du plus récent au plus ancien. */
    suspend fun getMyTrees(
        page: Int = 1,
        limit: Int = 50,
    ): List<TreeDto>

    /**
     * Signale l'arbre (#129 lots B/C, ADR 0015). `kind` vaut `"MISIDENTIFIED"` (défaut) ou `"GONE"` —
     * toute autre valeur rend 400 INVALID_KIND. 403 SELF_REPORT sur son propre arbre, 404 arbre
     * inconnu, 409 ALREADY_REPORTED en doublon, 429 REPORT_QUOTA_EXCEEDED au-delà de 10/24h. `GONE`
     * bascule l'arbre en `UNAVAILABLE` au quorum (2 comptes distincts, 7 jours d'ancienneté).
     */
    suspend fun reportTree(
        id: Long,
        suggestedSpeciesId: Long? = null,
        kind: String = "MISIDENTIFIED",
    ): TreeDto

    /** Retire le signalement [kind] ouvert de l'appelant (`MISIDENTIFIED` par défaut). 404
     *  REPORT_NOT_FOUND si aucun. */
    suspend fun withdrawTreeReport(
        id: Long,
        kind: String = "MISIDENTIFIED",
    ): TreeDto

    /** Créateur seul : maintient l'identification, résout tous les signalements ouverts. 403 sinon. */
    suspend fun resolveTreeReports(id: Long): TreeDto

    /**
     * Ajoute une photo à l'arbre (#223, lot A). Rend 201 [TreePhotoDto] ; le héro de l'arbre
     * (`TreeDto.photoUrl`) suit la plus récente.
     */
    suspend fun uploadPhoto(
        treeId: Long,
        bytes: ByteArray,
        fileName: String = "photo.jpg",
        contentType: String = "image/jpeg",
        onProgress: ((sent: Long, total: Long?) -> Unit)? = null,
    ): TreePhotoDto

    /**
     * Supprime une photo par son id de ligne (`TreePhotoDto.id`, PAS l'UUID de clé de stockage
     * visible dans les URLs). Auteur seul : 403 sinon, 404 si l'id est inconnu.
     */
    suspend fun deletePhoto(photoId: String)

    /**
     * Signale une photo par les pairs (#223 lot D, ADR 0015 réduit). Masquage automatique et
     * immédiat au premier signalement (N=1) : 204 sans body. 403 `SELF_REPORT` sur sa propre
     * photo, 404 `PHOTO_NOT_FOUND` si l'id est inconnu OU si la photo est déjà masquée (une photo
     * masquée est indiscernable d'une photo absente — pas d'oracle sur l'état de modération), 409
     * `ALREADY_REPORTED` seulement en course concurrente (le masquage à N=1 rend un re-signalement
     * séquentiel 404, pas 409), 429 `REPORT_QUOTA_EXCEEDED` au-delà du quota commun avec
     * `reportTree` (10/24h).
     */
    suspend fun reportPhoto(photoId: String)
}

/**
 * Implementation of TreeApi using Ktor HttpClient.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class TreeApiImpl(
    private val httpClient: HttpClient,
) : TreeApi {
    override suspend fun getNearbyTrees(
        lat: Double,
        lon: Double,
        radius: Double,
        page: Int,
        limit: Int,
        form: SpeciesFormDto?,
        part: HarvestPartDto?,
        inSeason: Boolean,
        rollupId: Long?,
        neverSeen: Boolean,
        bbox: String?,
    ): TreeListResponse =
        httpClient
            .get("${ApiPaths.V1}/trees") {
                parameter("lat", lat)
                parameter("lon", lon)
                parameter("radius", radius)
                parameter("page", page)
                parameter("limit", limit)
                form?.let { parameter("form", it.name) }
                part?.let { parameter("part", it.name) }
                if (inSeason) parameter("inSeason", true)
                rollupId?.let { parameter("rollupId", it) }
                if (neverSeen) parameter("neverSeen", true)
                bbox?.let { parameter("bbox", it) }
            }.body()

    override suspend fun getTreeById(id: Long): TreeDto = httpClient.get("${ApiPaths.V1}/trees/$id").body()

    override suspend fun sharePreview(id: Long): SharePreviewDto =
        httpClient.get("${ApiPaths.V1}/trees/$id/share-preview").body()

    override suspend fun createTree(
        request: CreateTreeRequest,
        idempotencyKey: String,
    ): TreeDto =
        httpClient
            .post("${ApiPaths.V1}/trees") {
                header("Idempotency-Key", idempotencyKey)
                setBody(request)
            }.body()

    override suspend fun updateTree(
        id: Long,
        request: UpdateTreeRequest,
    ): TreeDto =
        httpClient
            .put("${ApiPaths.V1}/trees/$id") {
                setBody(request)
            }.body()

    override suspend fun deleteTree(id: Long) {
        httpClient.delete("${ApiPaths.V1}/trees/$id")
    }

    override suspend fun observeTreeStatus(
        id: Long,
        status: TreeStatusDto,
    ): TreeStatusObservationDto =
        httpClient
            .post("${ApiPaths.V1}/trees/$id/status-observation") {
                contentType(ContentType.Application.Json)
                setBody(ObserveTreeStatusRequest(status))
            }.body()

    override suspend fun addTreeTrait(
        id: Long,
        kind: TreeTraitKindDto,
    ) {
        httpClient.post("${ApiPaths.V1}/trees/$id/traits") {
            contentType(ContentType.Application.Json)
            setBody(AddTreeTraitRequest(kind))
        }
    }

    override suspend fun removeTreeTrait(
        id: Long,
        kind: TreeTraitKindDto,
    ) {
        httpClient.delete("${ApiPaths.V1}/trees/$id/traits/${kind.name}")
    }

    override suspend fun favoriteTree(id: Long) {
        httpClient.post("${ApiPaths.V1}/trees/$id/favorite")
    }

    override suspend fun unfavoriteTree(id: Long) {
        httpClient.delete("${ApiPaths.V1}/trees/$id/favorite")
    }

    override suspend fun getFavoriteTrees(
        page: Int,
        limit: Int,
    ): List<TreeDto> {
        val response: TreeListResponse =
            httpClient
                .get("${ApiPaths.V1}/trees/favorites") {
                    parameter("page", page)
                    parameter("limit", limit)
                }.body()
        return response.trees
    }

    override suspend fun getMyTrees(
        page: Int,
        limit: Int,
    ): List<TreeDto> {
        val response: TreeListResponse =
            httpClient
                .get("${ApiPaths.V1}/trees/mine") {
                    parameter("page", page)
                    parameter("limit", limit)
                }.body()
        return response.trees
    }

    override suspend fun reportTree(
        id: Long,
        suggestedSpeciesId: Long?,
        kind: String,
    ): TreeDto =
        httpClient
            .post("${ApiPaths.V1}/trees/$id/reports") {
                setBody(CreateTreeReportRequest(kind = kind, suggestedSpeciesId = suggestedSpeciesId))
            }.body()

    override suspend fun withdrawTreeReport(
        id: Long,
        kind: String,
    ): TreeDto =
        httpClient
            .delete("${ApiPaths.V1}/trees/$id/reports") {
                parameter("kind", kind)
            }.body()

    override suspend fun resolveTreeReports(id: Long): TreeDto =
        httpClient
            .post("${ApiPaths.V1}/trees/$id/reports/resolve") {
                setBody(ResolveTreeReportsRequest(action = TreeReportResolveActionDto.MAINTAIN))
            }.body()

    override suspend fun uploadPhoto(
        treeId: Long,
        bytes: ByteArray,
        fileName: String,
        contentType: String,
        onProgress: ((sent: Long, total: Long?) -> Unit)?,
    ): TreePhotoDto =
        httpClient
            .submitFormWithBinaryData(
                url = "${ApiPaths.V1}/trees/$treeId/photos",
                formData =
                    formData {
                        append(
                            "photo",
                            bytes,
                            Headers.build {
                                append(HttpHeaders.ContentType, contentType)
                                append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                            },
                        )
                    },
            ) {
                if (onProgress != null) onUpload { sent, total -> onProgress(sent, total) }
            }.body()

    override suspend fun deletePhoto(photoId: String) {
        httpClient.delete("${ApiPaths.V1}/photos/$photoId")
    }

    override suspend fun reportPhoto(photoId: String) {
        httpClient.post("${ApiPaths.V1}/photos/$photoId/report")
    }
}
